import { useState, useEffect } from "react";
import Connect from "./views/Connect";
import Simulator from "./views/Simulator";
import { LogoWordmark } from "./components/LogoWordmark";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  decodeWizardShareLink,
  fetchSnapshot,
  snapshotToUserData,
} from "./components/shareLink";
import {
  generateHoursFromWizard,
} from "./data-sources/wizardGenerator";
import { enrichCsvHours } from "./api/client";
import type { UserData } from "./types";

const KNOWN_PATHS = new Set(["/", "/auth/callback", "/index.html"]);

export default function App() {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isUnknownPath, setIsUnknownPath] = useState<boolean>(false);
  const [sharedScenarioOverride, setSharedScenarioOverride] = useState<
    | {
        battery: import("./sim/types").BatteryConfig;
        strategy: import("./sim/types").Strategy;
        grid: import("./sim/types").GridFeeConfig;
      }
    | null
  >(null);
  const [loadingShared, setLoadingShared] = useState<boolean>(false);
  const [shareError, setShareError] = useState<string | null>(null);

  useEffect(() => {
    const path = window.location.pathname;
    if (!KNOWN_PATHS.has(path)) {
      setIsUnknownPath(true);
      return;
    }

    // Kolla efter delad kalkyl i URL. Två format:
    //   ?s=...        wizard-delning (inline, regenereras lokalt)
    //   ?delning=...  server-snapshot (Tibber/CSV, hämtas från backend)
    const params = new URLSearchParams(window.location.search);
    const wizardShared = params.get("s");
    const snapshotSlug = params.get("delning");

    function clearShareParams() {
      const url = new URL(window.location.href);
      url.searchParams.delete("s");
      url.searchParams.delete("delning");
      window.history.replaceState({}, "", url.toString());
    }

    // Snapshot-läge (Tibber/CSV)
    if (snapshotSlug) {
      setLoadingShared(true);
      (async () => {
        try {
          const snap = await fetchSnapshot(snapshotSlug);
          const ud = snapshotToUserData(snap);
          setUserData(ud);
          if (snap.scenario) {
            setSharedScenarioOverride(
              snap.scenario as typeof sharedScenarioOverride,
            );
          }
          clearShareParams();
        } catch (err) {
          setShareError(
            err instanceof Error
              ? err.message
              : "Kunde inte ladda den delade kalkylen.",
          );
          clearShareParams();
        } finally {
          setLoadingShared(false);
        }
      })();
      return;
    }

    if (!wizardShared) return;

    const decoded = decodeWizardShareLink(wizardShared);
    if (!decoded) {
      setShareError(
        "Länken kunde inte läsas. Den kan vara felaktig eller från en äldre version.",
      );
      clearShareParams();
      return;
    }

    setLoadingShared(true);
    (async () => {
      try {
        const generated = generateHoursFromWizard(decoded.wizardInput);
        const enriched = await enrichCsvHours(
          decoded.wizardInput.priceArea,
          generated.hours.map((h) => ({
            from: h.from,
            importKWh: h.importKWh,
            exportKWh: h.exportKWh,
          })),
        );

        const newUserData: UserData = {
          metadata: {
            source: "profile-estimate",
            fetchedAt: new Date().toISOString(),
            resolution: enriched.hours[0]?.quarterPrices ? "quarter" : "hour",
            quality: "modeled",
            periodFrom: enriched.metadata.firstTimestamp,
            periodTo: enriched.metadata.lastTimestamp,
            hourCount: enriched.metadata.totalHours,
            totalImport: generated.diagnostics.actualAnnualImportKWh,
            totalExport: generated.diagnostics.actualAnnualExportKWh,
            hoursWithMissingPrice: enriched.metadata.hoursWithMissingPrice,
            tibberHome: {
              id: `shared-${crypto.randomUUID()}`,
              nickname: null,
              address: { address1: null, postalCode: null, city: null },
              gridArea: null,
              priceArea: decoded.wizardInput.priceArea,
            },
            wizardInput: decoded.wizardInput,
          },
          hours: enriched.hours.map((h) => ({
            from: h.from,
            importKWh: h.importKWh,
            exportKWh: h.exportKWh,
            spotPrice: h.spotPrice,
            ...(h.quarterPrices && { quarterPrices: h.quarterPrices }),
          })),
        };
        setUserData(newUserData);
        setSharedScenarioOverride(decoded.scenario);
        clearShareParams();
      } catch (err) {
        setShareError(
          "Kunde inte ladda den delade kalkylen. Försök igen om en stund.",
        );
        console.error(err);
      } finally {
        setLoadingShared(false);
      }
    })();
  }, []);

  if (isUnknownPath) {
    return (
      <ErrorBoundary>
        <div className="page">
          <main className="content not-found">
            <LogoWordmark size="hero" />
            <h1>Sidan hittas inte</h1>
            <p>
              Den här länken leder inte till någon sida i appen. Det kan
              vara ett gammalt utseende på URL:en eller ett tryckfel.
            </p>
            <a href="/" className="not-found-link">
              Gå till startsidan
            </a>
          </main>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="page">
        <main className={userData ? "content content-wide" : "content"}>
          <header>
            <LogoWordmark size="hero" withTagline />
            {!userData && (
              <>
                <h1>Räkna på batterilagring innan säljaren räknar åt dig.</h1>
                <p className="lede">
                  Realistisk besparing och återbetalningstid baserat på din
                  egen elförbrukning och faktiska spotpriser.
                </p>
              </>
            )}
          </header>

          {userData ? (
            <Simulator
              userData={userData}
              onLogout={() => setUserData(null)}
              onUserDataUpdated={setUserData}
              {...(sharedScenarioOverride && {
                initialScenarioOverride: sharedScenarioOverride,
              })}
            />
          ) : loadingShared ? (
            <div className="loading-shared">
              <p>Laddar delad kalkyl…</p>
            </div>
          ) : (
            <>
              {shareError && <p className="error">{shareError}</p>}
              <Connect onDataLoaded={setUserData} />
            </>
          )}
        </main>
      </div>
    </ErrorBoundary>
  );
}
